'use client';

import { medicationDetectionsAtomFamily } from '@/atoms/medicationHighlightsAtoms';
import { findTextPositions } from '@heidi/editor/core';
import type { MedicationDetection } from '@heidi/growth/contracts';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { Plugin, PluginKey, type Transaction } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { getDefaultStore } from 'jotai';
import {
  DATA_MED_INDEX,
  MEDICATION_HIGHLIGHTS_CLASS,
  MEDICATION_HIGHLIGHTS_CLASSES,
} from './constants';

// Shared decoration plugin for markdown-native + vMarkdown/v1 twins. Jotai via getDefaultStore().

export interface MedicationHighlightsPluginState {
  sessionId: string;
  decorations: DecorationSet;
}

export type MedicationHighlightsRebuildMeta = { rebuild: true; sessionId: string };

export const medicationHighlightsPluginKey =
  new PluginKey<MedicationHighlightsPluginState>('medicationHighlights');

export function medicationHighlightsRebuildMeta(
  sessionId: string
): MedicationHighlightsRebuildMeta {
  return { rebuild: true, sessionId };
}

function isMedicationHighlightsRebuildMeta(
  meta: unknown
): meta is MedicationHighlightsRebuildMeta {
  if (typeof meta !== 'object' || meta === null) return false;
  const candidate = meta as Partial<MedicationHighlightsRebuildMeta>;
  return candidate.rebuild === true && typeof candidate.sessionId === 'string';
}

function getMedicationHighlightsRebuildMeta(
  tr: Transaction
): MedicationHighlightsRebuildMeta | undefined {
  const meta: unknown = tr.getMeta(medicationHighlightsPluginKey);
  return isMedicationHighlightsRebuildMeta(meta) ? meta : undefined;
}

function docTextInRange(doc: ProseMirrorNode, from: number, to: number): string {
  if (from < 0 || to > doc.content.size || from >= to) return '';
  return doc.textBetween(from, to, '');
}

function clampSpan(
  doc: ProseMirrorNode,
  spanStart: number,
  spanEnd: number
): { from: number; to: number } | null {
  const docSize = doc.content.size;
  const from = Math.max(0, Math.min(spanStart, docSize));
  const to = Math.max(0, Math.min(spanEnd, docSize));
  if (from >= to) return null;
  return { from, to };
}

function positionsForDetection(
  doc: ProseMirrorNode,
  detection: MedicationDetection,
  fallbackCache: Map<string, { from: number; to: number }[]>
): { from: number; to: number }[] {
  const matchedText = detection.matched_text.trim();
  if (!matchedText) return [];

  const clamped = clampSpan(doc, detection.span_start, detection.span_end);
  if (clamped) {
    const slice = docTextInRange(doc, clamped.from, clamped.to);
    if (slice === detection.matched_text) {
      return [clamped];
    }
  }

  const cached = fallbackCache.get(matchedText);
  if (cached) return cached;

  const positions = findTextPositions(doc, matchedText);
  fallbackCache.set(matchedText, positions);
  return positions;
}

function detectionsForSession(sessionId: string): MedicationDetection[] {
  if (!sessionId) return [];
  return getDefaultStore().get(medicationDetectionsAtomFamily(sessionId)) ?? [];
}

export function buildMedicationHighlightsDecorations(
  doc: ProseMirrorNode,
  detections: MedicationDetection[]
): DecorationSet {
  if (detections.length === 0) {
    return DecorationSet.empty;
  }

  const decorations: Decoration[] = [];
  const fallbackCache = new Map<string, { from: number; to: number }[]>();

  detections.forEach((detection, index) => {
    const positions = positionsForDetection(doc, detection, fallbackCache);
    positions.forEach(({ from, to }) => {
      decorations.push(
        Decoration.inline(from, to, {
          class: MEDICATION_HIGHLIGHTS_CLASSES,
          [DATA_MED_INDEX]: String(index),
        })
      );
    });
  });

  return DecorationSet.create(doc, decorations);
}

export function createMedicationHighlightsPlugin(): Plugin {
  return new Plugin({
    key: medicationHighlightsPluginKey,
    state: {
      init: () => ({
        sessionId: '',
        decorations: DecorationSet.empty,
      }),
      apply: (tr, value) => {
        const rebuildMeta = getMedicationHighlightsRebuildMeta(tr);
        const sessionId = rebuildMeta?.sessionId ?? value.sessionId;

        if (rebuildMeta) {
          return {
            sessionId,
            decorations: buildMedicationHighlightsDecorations(
              tr.doc,
              detectionsForSession(sessionId)
            ),
          };
        }

        if (tr.docChanged) {
          return {
            sessionId,
            decorations: value.decorations.map(tr.mapping, tr.doc),
          };
        }

        return { ...value, sessionId };
      },
    },
    props: {
      decorations(state) {
        return medicationHighlightsPluginKey.getState(state)?.decorations;
      },
    },
  });
}

export { MEDICATION_HIGHLIGHTS_CLASS };
